import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { CodeLoginResponse, MeResponse } from '@aichat/shared'
import { api, ApiError } from '@/shared/api'

/**
 * Кто вошёл и в каком клиенте работает (docs/05 §5.1).
 *
 * `/auth/me` вызывается один раз на загрузку SPA; роутер по нему решает,
 * пускать ли в `/app`.
 */
export const useSessionStore = defineStore('session', () => {
  const me = ref<MeResponse | null>(null)
  const loaded = ref(false)
  const loading = ref(false)

  const isAuthenticated = computed(() => me.value !== null)
  const tenant = computed(() => me.value?.current_tenant ?? null)
  const role = computed(() => me.value?.role ?? null)
  const isOwner = computed(() => role.value === 'owner')
  /** Администратор сервиса: ему открыта `/admin` (этап 4б). */
  const isAdmin = computed(() => me.value?.user.is_admin ?? false)
  /** Партнёр сервиса: в меню появляется «Мои клиенты» (этап 6е). */
  const isPartner = computed(() => me.value?.user.is_partner ?? false)
  const impersonated = computed(() => me.value?.impersonated ?? false)
  /** Вход не закончен: сервер ждёт код второго фактора (docs/09 §9.6). */
  const totpPending = computed(() => me.value?.totp?.pending ?? false)
  const totp = computed(() => me.value?.totp ?? { enabled: false, required: false, pending: false })

  async function load(force = false): Promise<MeResponse | null> {
    if (loaded.value && !force) return me.value
    loading.value = true
    try {
      me.value = await api.get<MeResponse>('/auth/me')
    } catch (err) {
      if (err instanceof ApiError && (err.needsLogin || err.status === 403)) me.value = null
      else throw err
    } finally {
      loaded.value = true
      loading.value = false
    }
    return me.value
  }

  /** Запросить ссылку входа. Ответ одинаковый для любого адреса (docs/09 §9.6). */
  async function requestMagicLink(email: string): Promise<void> {
    await api.post('/auth/magic-link', { email })
  }

  /**
   * Вход кодом из того же письма: ссылку ломают корпоративные фильтры
   * (решение 08.10.2026). Возвращает, куда вести дальше.
   */
  async function loginWithCode(email: string, code: string): Promise<string> {
    const res = await api.post<CodeLoginResponse>('/auth/code', { email, code })
    await load(true)
    return res.next
  }

  async function switchTenant(tenantId: string): Promise<void> {
    await api.post('/auth/switch-tenant', { tenant_id: tenantId })
    await load(true)
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } finally {
      me.value = null
    }
  }

  /**
   * Выйти из режима «войти как клиент» и вернуться в админку (docs/06 §6.2).
   *
   * Переход — обычной навигацией, а не роутером: cookie кабинета на сервере
   * подменяется обратно на администраторскую, и SPA должна перезагрузиться,
   * чтобы спросить `/auth/me` заново.
   */
  async function stopImpersonation(): Promise<void> {
    const res = await api.post<{ next: string }>('/auth/impersonation/stop')
    me.value = null
    location.assign(res.next)
  }

  /** Сессия отвалилась на любом запросе — чтобы роутер увёл на вход. */
  function forget(): void {
    me.value = null
  }

  return {
    me,
    loaded,
    loading,
    isAuthenticated,
    tenant,
    role,
    isOwner,
    isAdmin,
    isPartner,
    impersonated,
    totpPending,
    totp,
    load,
    requestMagicLink,
    loginWithCode,
    switchTenant,
    logout,
    stopImpersonation,
    forget,
  }
})
